import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Button,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";

const API_BASE_URL = "https://api.hr-ebnhaian.cloud/api/v1";

const Section = ({ title, children }) => (
  <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
    <Typography fontWeight="bold">{title}</Typography>
    <Divider sx={{ my: 2 }} />
    {children}
  </Paper>
);

const FileUpload = ({
  label,
  valueFile,
  onPickFile,
  fileNameValue,
  onFileNameChange,
  accept, // ✅ NEW
}) => {
  return (
    <Box>
      <Typography fontSize={13} fontWeight="bold" mb={1}>
        {label}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Button component="label" variant="outlined" fullWidth>
            Upload File
            <input
              hidden
              type="file"
              accept={accept} // ✅ NEW
              onChange={(e) => onPickFile(e.target.files?.[0] || null)}
            />
          </Button>

          {valueFile && (
            <Typography fontSize={12} mt={1}>
              Selected: <b>{valueFile.name}</b>
            </Typography>
          )}

          {accept && (
            <Typography fontSize={11} mt={0.5} sx={{ opacity: 0.65 }}>
              Allowed: {accept}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="File name (optional)"
            fullWidth
            value={fileNameValue}
            onChange={(e) => onFileNameChange(e.target.value)}
            helperText="If empty, original file name will be used."
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default function AddCenter() {
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const schema = Yup.object({
    name: Yup.string().required("Center name is required"),
    code: Yup.string().required("Center code is required"),
    phone: Yup.string().required("Phone is required"),
    mapLink: Yup.string().required("Map link is required"),
  });

  const formik = useFormik({
    initialValues: {
      // BASIC
      name: "",
      code: "",
      phone: "",
      email: "",
      address: "",
      mapLink: "",

      // RENTAL
      rentalContract: {
        file: null,
        startDate: "",
        endDate: "",
        fileName: "",
      },

      // FIRE
      fireSafetyContract: {
        file: null,
        startDate: "",
        endDate: "",
        fileName: "",
      },

      // LICENSE ✅ (issueDate / expiryDate)
      healthLicense: {
        file: null,
        issueDate: "",
        expiryDate: "",
        authority: "",
        licenseNumber: "",
        fileName: "",
      },

      notes: "",
    },

    validationSchema: schema,

    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const fd = new FormData();

        /* =====================
           BASIC
        ===================== */
        fd.append("name", values.name);
        fd.append("code", values.code);
        fd.append("phone", values.phone);
        fd.append("mapLink", values.mapLink);

        if (values.email) fd.append("email", values.email);
        if (values.address) fd.append("address", values.address);
        if (values.notes) fd.append("notes", values.notes);

        // ✅ مهم: إنشاء المركز دائمًا INACTIVE
        fd.append("status", "INACTIVE");

        /* =====================
           RENTAL CONTRACT
        ===================== */
        if (
          values.rentalContract.file ||
          values.rentalContract.startDate ||
          values.rentalContract.endDate
        ) {
          if (values.rentalContract.file)
            fd.append("rentalContract[file]", values.rentalContract.file);

          if (values.rentalContract.startDate)
            fd.append("rentalContract[startDate]", values.rentalContract.startDate);

          if (values.rentalContract.endDate)
            fd.append("rentalContract[endDate]", values.rentalContract.endDate);

          if (values.rentalContract.file) {
            const displayName =
              values.rentalContract.fileName?.trim() ||
              values.rentalContract.file.name;
            fd.append("rentalContract[fileName]", displayName);
          }
        }

        /* =====================
           FIRE SAFETY CONTRACT
        ===================== */
        if (
          values.fireSafetyContract.file ||
          values.fireSafetyContract.startDate ||
          values.fireSafetyContract.endDate
        ) {
          if (values.fireSafetyContract.file)
            fd.append("fireSafetyContract[file]", values.fireSafetyContract.file);

          if (values.fireSafetyContract.startDate)
            fd.append(
              "fireSafetyContract[startDate]",
              values.fireSafetyContract.startDate
            );

          if (values.fireSafetyContract.endDate)
            fd.append(
              "fireSafetyContract[endDate]",
              values.fireSafetyContract.endDate
            );

          if (values.fireSafetyContract.file) {
            const displayName =
              values.fireSafetyContract.fileName?.trim() ||
              values.fireSafetyContract.file.name;
            fd.append("fireSafetyContract[fileName]", displayName);
          }
        }

        /* =====================
           HEALTH LICENSE ✅ (FIXED)
        ===================== */
        const hasLicenseBlock =
          values.healthLicense.file ||
          values.healthLicense.issueDate ||
          values.healthLicense.expiryDate ||
          values.healthLicense.authority ||
          values.healthLicense.licenseNumber;

        // ✅ إذا بلشت تعبي الرخصة لازم تكملها كلها
        if (hasLicenseBlock) {
          if (
            !values.healthLicense.file ||
            !values.healthLicense.issueDate ||
            !values.healthLicense.expiryDate
          ) {
            setSnack({
              open: true,
              message: "Health license needs file + issue date + expiry date",
              severity: "error",
            });
            return;
          }

          fd.append("healthLicense[file]", values.healthLicense.file);
          fd.append("healthLicense[issueDate]", values.healthLicense.issueDate);
          fd.append("healthLicense[expiryDate]", values.healthLicense.expiryDate);

          if (values.healthLicense.authority)
            fd.append("healthLicense[authority]", values.healthLicense.authority);

          if (values.healthLicense.licenseNumber)
            fd.append(
              "healthLicense[licenseNumber]",
              values.healthLicense.licenseNumber
            );

          const displayName =
            values.healthLicense.fileName?.trim() || values.healthLicense.file.name;
          fd.append("healthLicense[fileName]", displayName);
        }

        await axios.post(`${API_BASE_URL}/centers`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        setSnack({
          open: true,
          message: "Center added successfully ✅",
          severity: "success",
        });

        resetForm();
      } catch (err) {
        setSnack({
          open: true,
          message: err.response?.data?.message || "Unexpected error occurred ❌",
          severity: "error",
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Box p={3} sx={{ background: "#f4f6fa", minHeight: "100vh" }}>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Add Center
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        {/* BASIC */}
        <Section title="Basic Information">
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Center Name"
                fullWidth
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Center Code"
                fullWidth
                name="code"
                value={formik.values.code}
                onChange={formik.handleChange}
                error={formik.touched.code && Boolean(formik.errors.code)}
                helperText={formik.touched.code && formik.errors.code}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography fontSize={12} mb={0.5}>
                Phone
              </Typography>
              <PhoneInput
                country="ae"
                value={formik.values.phone}
                onChange={(v) => formik.setFieldValue("phone", v)}
                inputStyle={{ width: "100%" }}
              />
              {formik.touched.phone && formik.errors.phone && (
                <Typography color="error" fontSize={12} mt={0.5}>
                  {formik.errors.phone}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Section>

        {/* LOCATION */}
        <Section title="Contact & Location">
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Email"
                fullWidth
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Map Link"
                fullWidth
                name="mapLink"
                value={formik.values.mapLink}
                onChange={formik.handleChange}
                error={formik.touched.mapLink && Boolean(formik.errors.mapLink)}
                helperText={formik.touched.mapLink && formik.errors.mapLink}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Address"
                fullWidth
                name="address"
                value={formik.values.address}
                onChange={formik.handleChange}
              />
            </Grid>
          </Grid>
        </Section>

        {/* RENTAL */}
        <Section title="Rental Contract">
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={formik.values.rentalContract.startDate}
                onChange={(e) =>
                  formik.setFieldValue("rentalContract.startDate", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="End Date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={formik.values.rentalContract.endDate}
                onChange={(e) =>
                  formik.setFieldValue("rentalContract.endDate", e.target.value)
                }
              />
            </Grid>
          </Grid>

          <FileUpload
            label="Rental Contract File"
            accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx"
            valueFile={formik.values.rentalContract.file}
            onPickFile={(file) => formik.setFieldValue("rentalContract.file", file)}
            fileNameValue={formik.values.rentalContract.fileName}
            onFileNameChange={(v) => formik.setFieldValue("rentalContract.fileName", v)}
          />
        </Section>

        {/* FIRE */}
        <Section title="Fire Safety Contract">
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={formik.values.fireSafetyContract.startDate}
                onChange={(e) =>
                  formik.setFieldValue(
                    "fireSafetyContract.startDate",
                    e.target.value
                  )
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="End Date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={formik.values.fireSafetyContract.endDate}
                onChange={(e) =>
                  formik.setFieldValue("fireSafetyContract.endDate", e.target.value)
                }
              />
            </Grid>
          </Grid>

          <FileUpload
            label="Fire Safety Contract File"
            accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx"
            valueFile={formik.values.fireSafetyContract.file}
            onPickFile={(file) =>
              formik.setFieldValue("fireSafetyContract.file", file)
            }
            fileNameValue={formik.values.fireSafetyContract.fileName}
            onFileNameChange={(v) =>
              formik.setFieldValue("fireSafetyContract.fileName", v)
            }
          />
        </Section>

        {/* LICENSE */}
        <Section title="Health License">
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="Issue Date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={formik.values.healthLicense.issueDate}
                onChange={(e) =>
                  formik.setFieldValue("healthLicense.issueDate", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="Expiry Date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={formik.values.healthLicense.expiryDate}
                onChange={(e) =>
                  formik.setFieldValue("healthLicense.expiryDate", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="Authority (DHA/MOH/...)"
                fullWidth
                value={formik.values.healthLicense.authority}
                onChange={(e) =>
                  formik.setFieldValue("healthLicense.authority", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="License Number"
                fullWidth
                value={formik.values.healthLicense.licenseNumber}
                onChange={(e) =>
                  formik.setFieldValue("healthLicense.licenseNumber", e.target.value)
                }
              />
            </Grid>
          </Grid>

          <FileUpload
            label="Health License File"
            accept=".pdf,.jpg,.jpeg,.png"
            valueFile={formik.values.healthLicense.file}
            onPickFile={(file) => formik.setFieldValue("healthLicense.file", file)}
            fileNameValue={formik.values.healthLicense.fileName}
            onFileNameChange={(v) => formik.setFieldValue("healthLicense.fileName", v)}
          />
        </Section>

        {/* NOTES */}
        <Section title="Notes">
          <TextField
            fullWidth
            multiline
            rows={4}
            name="notes"
            value={formik.values.notes}
            onChange={formik.handleChange}
          />
        </Section>

        <Button
          variant="contained"
          color="success"
          type="submit"
          disabled={formik.isSubmitting}
        >
          Save Center
        </Button>
      </form>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack({ ...snack, open: false })}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
